/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { IconButton, Link, Typography2 } from '@coursera/cds-core';
import { ExternalLinkIcon, NoteIcon } from '@coursera/cds-icons';

import ExternalLinksWrapper from 'bundles/assess-common/components/ExternalLinksWrapper';

import _t from 'i18n!nls/assess-common';

type Props = {
  url: string;
  disabled: boolean;
  urlTitle?: string;
  onRemoveEmbeddedUrl?: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void;
};

const styles = {
  container: () => css`
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    background-color: var(--cds-color-neutral-background-primary-weak);
    border-radius: var(--cds-border-radius-50);
    position: relative;
    display: inline-flex;
    align-items: center;
    max-width: 100%;
    overflow: hidden;
  `,

  icon: () => css`
    color: var(--cds-color-neutral-primary);
    flex-shrink: 0;
  `,

  link: () => css`
    padding: var(--cds-spacing-150);
    text-decoration: none;
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-grow: 1;
    min-width: 0;

    &:hover {
      background-color: var(--cds-color-interactive-background-primary-hover-weak);
    }
  `,

  urlTitle: () => css`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    margin-left: var(--cds-spacing-100);
  `,

  iconButton: () => css`
    flex-shrink: 0;
    width: 40px;
  `,
};

const EmbeddedUrlBlock = ({ url, urlTitle, disabled, onRemoveEmbeddedUrl }: Props) => {
  return (
    <div css={styles.container} data-testid="embedded-url-block">
      <ExternalLinksWrapper>
        <Link
          rel="noopener noreferrer"
          target="_blank"
          href={url}
          aria-label={_t('Open URL #{urlTitle}', { urlTitle })}
          css={styles.link}
          data-testid="embedded-url-block-link"
        >
          {disabled ? <ExternalLinkIcon size="small" css={styles.icon} /> : <NoteIcon size="small" css={styles.icon} />}
          {urlTitle && (
            <Typography2
              variant="actionSecondary"
              component="span"
              css={styles.urlTitle}
              data-testid="embedded-url-block-filename"
            >
              {urlTitle}
            </Typography2>
          )}
        </Link>
      </ExternalLinksWrapper>
      {!disabled && (
        <IconButton
          variant="ghost"
          intent="delete"
          size="small"
          onClick={onRemoveEmbeddedUrl}
          onKeyUp={onRemoveEmbeddedUrl}
          tabIndex={0}
          aria-label={_t('Remove URL')}
          css={styles.iconButton}
        />
      )}
    </div>
  );
};

export default EmbeddedUrlBlock;
